import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Divider} from 'antd';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {t, isRTL} from '../../../../../../services/i18n';
import {Colors} from '../../../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import {sortCourseEventDays} from '../../../../../../services/Helpers';
import {fetchApi} from '../../../../../../services/HTTP';
import {setHideFooter} from '../../../../../../redux/stepperSlice';
import VStack from '../../../../../common/Layout/VStack';
import HStack from '../../../../../common/Layout/HStack';
import Box from '../../../../../common/Layout/Box';
import AppText from '../../../../../common/AppText';
import FiltersHandler from '../../../../../common/FiltersHandler';
import ChooseHugToSwapSkeleton from './ChooseHugToSwapSkeleton';
import RadioListItem from '../../../../../common/RadioListItem';
import InnerStepSection from '../../../../../common/InnerStepSection';
import {getSwapDayFiltersConfig} from '../ChooseSwapDay/swapDayFiltersConfig';

const swapDayPillStyle = {
    backgroundColor: '#F7F7F7',
    borderRadius: '4px',
    padding: '5px 10px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
};

const listContainerStyle = {
    borderTop: `1px solid ${Colors.borderColor}`,
    borderBottom: `1px solid ${Colors.borderColor}`,
    marginTop: '12px',
};

const spotsRemainingBadgeStyle = {
    backgroundColor: Colors.newDesign.lightGreen,
    padding: '2px 8px',
    borderRadius: '2px 2px 2px 0',
    flexShrink: 0,
};

const ChooseSwapDay = ({target, values, setFieldValue}) => {
    const dispatch = useDispatch();
    const lang = useSelector(state => state.userSettings.lang);

    const [isLoading, setIsLoading] = useState(true);
    const [swapData, setSwapData] = useState(null);
    const [filteredToDays, setFilteredToDays] = useState([]);

    const boxLocale = useMemo(() => (lang?.language === 'Hebrew' ? 'he' : 'en'), [lang]);

    const filtersConfig = useMemo(() => getSwapDayFiltersConfig(), []);

    const currentDays = useMemo(() => (swapData?.current_days ? sortCourseEventDays(swapData.current_days) : []), [swapData]);

    const toDaysSource = useMemo(() => {
        if (!swapData?.all_days) {
            return [];
        }
        const fromId = values.fromSeriesEventId;
        return sortCourseEventDays(swapData.all_days).filter(
            day => day.id !== fromId && !currentDays.some(bookedDay => bookedDay.id === day.id),
        );
    }, [swapData, values.fromSeriesEventId, currentDays]);

    const fetchSeriesDays = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await fetchApi('hugim/getSeriesDaysForSwap', 'POST', {membership_user_id: target.membershipUser.id}, false, true);
            setSwapData(res);
            setFieldValue('swapDayData', res);
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            setIsLoading(false);
        }
    }, [target.membershipUser.id]);

    const formatSwapDayWeekdayLabel = (day, boxLocale) => {
        const weekday = moment().weekday(day.day_of_week).locale(boxLocale).format('dddd');
        return t('swap-day-on-weekday', {day: weekday});
    };

    const isFullBooking = !!swapData?.is_full_booking;

    useEffect(() => {
        fetchSeriesDays();
    }, [fetchSeriesDays]);

    useEffect(() => {
        if (!currentDays.length || values.fromSeriesEventId) {
            return;
        }
        setFieldValue('fromSeriesEventId', currentDays[0].id);
    }, [currentDays, values.fromSeriesEventId]);

    useEffect(() => {
        if (isFullBooking) {
            if (values.toSeriesEventId) {
                setFieldValue('toSeriesEventId', null);
            }
            return;
        }
        if (!filteredToDays.length) {
            if (values.toSeriesEventId) {
                setFieldValue('toSeriesEventId', null);
            }
            return;
        }
        if (!values.toSeriesEventId || !filteredToDays.some(day => day.id === values.toSeriesEventId)) {
            setFieldValue('toSeriesEventId', filteredToDays[0].id);
        }
    }, [filteredToDays, values.toSeriesEventId, isFullBooking]);

    const fromDay = useMemo(
        () => currentDays.find(day => day.id === values.fromSeriesEventId),
        [currentDays, values.fromSeriesEventId],
    );

    const toDay = useMemo(
        () => filteredToDays.find(day => day.id === values.toSeriesEventId) ?? toDaysSource.find(day => day.id === values.toSeriesEventId),
        [filteredToDays, toDaysSource, values.toSeriesEventId],
    );

    const fromLabel = fromDay ? formatSwapDayWeekdayLabel(fromDay, boxLocale) : null;
    const toLabel = toDay ? formatSwapDayWeekdayLabel(toDay, boxLocale) : null;

    const filtersHeader = !isFullBooking ? (
        <FiltersHandler
            list={toDaysSource}
            config={filtersConfig}
            onFiltered={(filtered) => setFilteredToDays(filtered)}
        />
    ) : null;

    if (isLoading) {
        return <ChooseHugToSwapSkeleton/>;
    }

    if (!swapData || !currentDays.length) {
        return (
            <VStack space={'1rem'} style={{padding: '2rem 0'}}>
                <AppText fontSize={GlobalStyleAttributes.fonts.standard16}>
                    {t('no-days-found-for-swap')}
                </AppText>
            </VStack>
        );
    }

    return (
        <VStack space={'1.5rem'}>
            <HStack alignItems={'center'} space={'0.5rem'} flexWrap={'wrap'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.darkGrey}>
                    {`${t('courseLimitationsCourseName')}:`}
                </AppText>
                <Box style={swapDayPillStyle}>
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.mainText}>
                        {swapData?.series_name ?? ''}
                    </AppText>
                </Box>
            </HStack>

            {isFullBooking ? (
                <VStack space={'20px'}>
                    <AppText
                        fontSize={GlobalStyleAttributes.fonts.standard16}
                        color={Colors.newDesign.darkGrey}
                        style={{paddingTop: '8px'}}
                    >
                        {t('swap-day-full-booking-error')}
                    </AppText>
                    <Divider style={{margin: '12px 0 0'}}/>
                </VStack>
                ) : (
                    <>
                    <InnerStepSection stepNumber={1} title={t('choose-swap-day-from-section')} showConnector>
                        <Divider style={{margin: '12px 0 0'}}/>
                        <VStack space={'0'} style={listContainerStyle}>
                            {currentDays.map((day, index) => (
                                <RadioListItem
                                key={day.id}
                                label={formatSwapDayWeekdayLabel(day, boxLocale)}
                                isSelected={values.fromSeriesEventId === day.id}
                                onSelect={() => setFieldValue('fromSeriesEventId', Number(day.id))}
                                showBottomBorder={index < currentDays.length - 1}
                                />
                                ))}
                        </VStack>
                    </InnerStepSection>
                    <InnerStepSection stepNumber={2} title={t('choose-swap-day-to-section')} headerExtra={filtersHeader}>
                        {!isFullBooking && fromLabel && (
                            <HStack alignItems={'center'} space={'0.5rem'} flexWrap={'wrap'} style={{paddingTop: '12px', rowGap: '0.5rem'}}>
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.darkGrey}>
                                    {t('replacing')}
                                </AppText>
                                <Box style={swapDayPillStyle}>
                                    <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.mainText}>
                                        {fromLabel}
                                    </AppText>
                                </Box>
                                {toLabel && (
                                    <>
                                        <FontAwesomeIcon
                                            icon={`fa-light fa-arrow-${isRTL() ? 'left' : 'right'}`}
                                            color={Colors.newDesign.darkGrey}
                                            fontSize={GlobalStyleAttributes.fonts.heading4}
                                        />
                                        <Box style={swapDayPillStyle}>
                                            <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.mainText}>
                                                {toLabel}
                                            </AppText>
                                        </Box>
                                    </>)}
                            </HStack>
                        )}
                        <Divider style={{margin: '12px 0 0'}}/>
                                <VStack space={'0'} style={listContainerStyle}>
                                    {filteredToDays.map((day, index) => (
                                        <RadioListItem
                                            key={day.id}
                                            label={formatSwapDayWeekdayLabel(day, boxLocale)}
                                            isSelected={values.toSeriesEventId === day.id}
                                            onSelect={() => setFieldValue('toSeriesEventId', Number(day.id))}
                                            showBottomBorder={index < filteredToDays.length - 1}
                                            endContent={day.spots_left != null && (
                                                <Box style={spotsRemainingBadgeStyle}>
                                                    <AppText fontSize={GlobalStyleAttributes.fonts.standard16} color={Colors.newDesign.mainGreen}>
                                                        {t('spots-remaining', {count: day.spots_left})}
                                                    </AppText>
                                                </Box>
                                            )}
                                        />
                                    ))}
                                    {filteredToDays.length === 0 && (
                                        <AppText fontSize={GlobalStyleAttributes.fonts.standard16} style={{padding: '1.5rem 12px'}}>
                                            {t('no-days-found-for-swap')}
                                        </AppText>
                                    )}
                                </VStack>
                                
                            </InnerStepSection>
                    </>
           )}
        </VStack>
    );
};

export default ChooseSwapDay;
